import { AlertCircle } from 'lucide-react';
import { Link, useLocation } from 'react-router';
import type { User } from '../types';
import { getProfileCompletionWidthClass, getUserProfileCompletion } from '../utils/userProfile';
import { Button } from './ui/button';
import { cn } from './ui/utils';

interface ProfileCompletionPromptProps {
  user: User | null | undefined;
  className?: string;
}

const joinLabels = (labels: string[]): string => {
  if (labels.length === 0) {
    return '';
  }

  if (labels.length === 1) {
    return labels[0];
  }

  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`;
  }

  return `${labels.slice(0, -1).join(', ')}, and ${labels.at(-1)}`;
};

export function ProfileCompletionPrompt({ user, className }: ProfileCompletionPromptProps) {
  const location = useLocation();
  const completion = getUserProfileCompletion(user);

  if (!user || completion.percent >= 100) {
    return null;
  }

  if (
    location.pathname === '/app/profile' ||
    location.pathname === '/app/settings/profile' ||
    location.pathname.startsWith('/app/users/')
  ) {
    return null;
  }

  const missingLabel = joinLabels(completion.missingFields.map((field) => field.label));

  return (
    <div className={cn('border-b border-chart-5/40 bg-chart-5/10', className)}>
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between lg:px-6">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <AlertCircle className="h-4 w-4 text-chart-5" />
            Profile completion {completion.percent}%
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Add {missingLabel} so teammates can reach you and your profile stays useful in search, staffing, and reporting.
          </p>
          <div className="mt-2 h-1.5 w-full max-w-md overflow-hidden rounded-full bg-chart-5/10">
            <div className={cn('h-full rounded-full bg-chart-5/10', getProfileCompletionWidthClass(completion.percent))} />
          </div>
        </div>

        <Button asChild size="sm">
          <Link to="/app/profile">Update profile</Link>
        </Button>
      </div>
    </div>
  );
}

export default ProfileCompletionPrompt;